/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Grid, Paper, Portal, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { find, isEmpty } from 'lodash'

import 'moment/locale/pl'

import { Car, CarDictionaries, DigitalCertificate } from '../api/index'
import ButtonBox from '../components/ButtonBox'
import CarDataTable from '../components/CarDataTable'
import Section from '../components/Section'
import SectionHeader from '../components/SectionHeader'
import useAlert from '../hooks/useAlert'
import useAuth from '../hooks/useAuth'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import {
  ArrowRight,
  CancelIcon,
  CarFrontIcon,
  CheckIcon,
  SaveIcon,
} from '../icons'

const StyledPaper = styled((props) => <Paper {...props} elevation={0} />)({
  height: `100%`,
})

function ControlsEditCar() {
  const { controlId, portalRef, onCancelClick } = useOutletContext()
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const { t } = useTranslation(['common', 'create'])
  const { setAlert } = useAlert()
  const {
    auth: { dealerId, refactory },
  } = useAuth()
  const isRefactory = refactory === 'true'

  const [car, setCar] = useState({})
  const [typesOpts, setTypesOpts] = useState([])
  const [makesOpts, setMakesOpts] = useState([])
  const [modelsOpts, setModelsOpts] = useState([])
  const [versionsOpts, setVersionsOpts] = useState([])
  const [isImgBtnLoading, setIsImgBtnLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saveBtnLoading, setSaveBtnLoading] = useState(false)
  const [isVinBtnLoading, setIsVinBtnLoading] = useState(false)

  useEffect(() => {
    const digitalCertificateId = sessionStorage.getItem('digitalCertificateId')

    const loadData = async () => {
      if (digitalCertificateId) {
        if (isEmpty(car)) {
          await fetchInitData()
        } else {
          await fetchData(car.typeId, car.makeId, car.modelId)
        }
      } else {
        await fetchTypes()
      }
      setLoading(false)
    }

    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchInitData = async () => {
    const digitalCertificateId = sessionStorage.getItem('digitalCertificateId')
    try {
      const { data } = await DigitalCertificate.getCertificate(
        axiosPrivate,
        digitalCertificateId,
      )

      setCar({ ...data })
      await fetchData(data.typeId, data.makeId, data.modelId)
    } catch (error) {
      // eslint-disable-next-line no-shadow, no-console
      console.error('Error fetching data:', error)
    }
  }

  const fetchData = async (typeId, makeId, modelId) => {
    await fetchTypes()
    if (typeId) await fetchMakes(typeId)
    if (makeId) await fetchModels(typeId, makeId)
    if (modelId) await fetchVersions(modelId)
  }

  const fetchTypes = async () => {
    try {
      const { data: typesData } = await CarDictionaries.getTypesDictionary(
        axiosPrivate,
      )
      setTypesOpts(typesData)
    } catch (error) {
      // eslint-disable-next-line no-shadow, no-console
      console.error('Error fetching data:', error)
    }
  }

  const fetchMakes = async (typeId) => {
    try {
      const { data: makesData } = await CarDictionaries.getMakesDictionary(
        axiosPrivate,
        typeId,
      )
      setMakesOpts(makesData)
    } catch (error) {
      // eslint-disable-next-line no-shadow, no-console
      console.error('Error fetching data:', error)
    }
  }

  const fetchModels = async (typeId, makeId) => {
    try {
      const { data: modelsData } = await CarDictionaries.getModelsDictionary(
        axiosPrivate,
        typeId,
        makeId,
      )
      setModelsOpts(modelsData)
    } catch (error) {
      // eslint-disable-next-line no-shadow, no-console
      console.error('Error fetching data:', error)
    }
  }

  const fetchVersions = async (modelId) => {
    try {
      const { data: versionsData } =
        await CarDictionaries.getVersionsDictionary(axiosPrivate, modelId)
      setVersionsOpts(versionsData)
    } catch (error) {
      // eslint-disable-next-line no-shadow, no-console
      console.error('Error fetching data:', error)
    }
  }

  const handleInputChange = (name, value) => {
    if (name === 'typeId') {
      fetchMakes(value)
      setCar({
        ...car,
        typeId: value,
        type: find(typesOpts, { id: value })?.name,
        make: '',
        makeId: 0,
        model: '',
        modelId: 0,
        version: '',
        ecCode: 0,
        fuel: '',
      })
    } else if (name === 'makeId') {
      fetchModels(car.typeId, value)
      setCar({
        ...car,
        makeId: value,
        make: find(makesOpts, { id: value })?.name,
        model: '',
        modelId: 0,
        version: '',
        ecCode: 0,
        fuel: '',
      })
    } else if (name === 'modelId') {
      if (!isRefactory) {
        fetchVersions(value)
      }
      setCar({
        ...car,
        modelId: value,
        model: find(modelsOpts, { id: value })?.name,
        ecCode: 0,
        version: '',
        fuel: '',
      })
    } else if (name === 'ecCode') {
      setCar({
        ...car,
        ecCode: value,
        version: find(versionsOpts, { id: value })?.name,
        fuel: find(versionsOpts, { id: value })?.fuel,
      })
    } else {
      setCar({ ...car, [name]: value })
    }
  }

  const handleImageChange = (e) => {
    const fileForm = new FormData()
    fileForm.append('file', e.target.files[0])
    setIsImgBtnLoading(true)
    DigitalCertificate.updateCertificateImage(axiosPrivate, controlId, fileForm)
      .then(({ data: { message } }) => {
        setIsImgBtnLoading(false)
        setCar({ ...car, imageUrl: message })
      })
      // eslint-disable-next-line no-shadow, no-console
      .catch((e) => console.error(e))
  }

  const saveCarBasicData = (redirect) => (e) => {
    e.preventDefault()
    setSaveBtnLoading(true)
    DigitalCertificate.updateCerificate(axiosPrivate, controlId, car)
      .then(() => {
        setSaveBtnLoading(false)
        if (redirect) {
          navigate(redirect)
        }
      })
      .catch(({ response }) => {
        const {
          data: { errors, message: defaultMessage },
          status,
        } = response

        if (errors?.[0]) {
          const { message: errorMessage, field: errorField } = errors[0]
          setAlert(`${errorMessage}: ${errorField}`, 'error')
        } else if (status === 409) {
          setAlert(
            t('create:car.error.alreadyExistEdit', { vin: car.vin }),
            'error',
          )
        } else {
          setAlert(`Error ${status}: ${defaultMessage}`, 'error')
        }

        setSaveBtnLoading(false)
      })
  }

  const getCarData = () => {
    if (!car.vin) return

    setIsVinBtnLoading(true)
    Car.getCar(axiosPrivate, car.vin, dealerId)
      .then(({ data }) => {
        setCar({
          ...car,
          imageUrl: data.imageUrl,
          typeId: data.typeId,
          makeId: data.makeId,
          modelId: data.modelId,
          mileage: data.mileage,
          ecCode: data.ecCode,
          make: data.make,
          model: data.model,
          version: data.version,
          fuel: data.fuel,
          color: data.color,
          registrationNumber: data.registrationNumber,
          manufacturingYear: data.manufacturingYear,
          firstRegistrationDate: data.firstRegistrationDate,
        })

        fetchData(data.typeId, data.makeId, data.modelId)
      })
      .catch(() => {
        setAlert(t('create:start.form.error.notExistAnnouncement'), 'error')
      })
      .finally(() => {
        setIsVinBtnLoading(false)
      })
  }

  const manualTypeFields = [
    {
      name: 'typeId',
      label: t('create:car.form.input.type.label'),
      value: car?.typeId,
      options: typesOpts,
      isEditable: true,
      isSelect: true,
      disabled: loading,
    },
    {
      name: 'makeId',
      label: t('create:car.form.input.make.label'),
      value: car?.makeId,
      options: makesOpts,
      isEditable: true,
      isSelect: true,
      disabled: loading || !car?.typeId,
    },
    {
      name: 'modelId',
      label: t('create:car.form.input.model.label'),
      value: car?.modelId,
      options: modelsOpts,
      isEditable: true,
      isSelect: true,
      disabled: loading || !car?.makeId,
    },
    ...(!isRefactory
      ? [
          {
            name: 'ecCode',
            label: t('create:car.form.input.version.label'),
            value: car?.ecCode,
            options: versionsOpts,
            isEditable: true,
            isSelect: true,
            disabled: loading || !car?.modelId,
          },
        ]
      : []),
  ]

  const commonFields = [
    {
      name: 'vin',
      label: t('create:car.form.input.vin.label'),
      value: car.vin,
      isEditable: true,
      endAdornment: !isRefactory && (
        <LoadingButton
          size="small"
          variant="contained"
          loading={isVinBtnLoading}
          onClick={getCarData}
          disabled={!car.vin}
          sx={{ ml: 1 }}
        >
          {t('button.searchVin')}
        </LoadingButton>
      ),
    },
    {
      name: 'registrationNumber',
      label: t('create:car.form.input.regNo.label'),
      value: car.registrationNumber,
      isEditable: true,
    },
    {
      name: 'firstRegistrationDate',
      label: t('create:car.form.input.firstRegistrationDate.label'),
      value: car.firstRegistrationDate,
      isEditable: true,
      isDate: true,
    },
  ]

  const additionalFields = [
    {
      name: 'mileage',
      label: t('create:car.form.input.mileage.label'),
      value: car.mileage,
      isEditable: true,
    },
    {
      name: 'color',
      label: t('create:car.form.input.color.label'),
      value: car.color,
      isEditable: true,
    },

    {
      name: 'manufacturingYear',
      label: t('create:car.form.input.manufacturingYear.label'),
      value: car.manufacturingYear,
      isEditable: true,
    },
  ]

  const carDataTable = [
    ...commonFields,
    ...(['Manual', undefined].includes(car?.source?.type)
      ? manualTypeFields
      : []),
    ...(isRefactory ? [] : additionalFields),
  ]

  return (
    <div>
      <Section>
        <SectionHeader
          icon={<CarFrontIcon />}
          title={t('carBasicDataTitle')}
          subtitle={t('carBasicDataSubTitle')}
        />
        <Grid container spacing={5}>
          {!isRefactory && (
            <Grid item xs={12} lg={6}>
              <StyledPaper>
                <Box
                  sx={{
                    padding: 3,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="h3" sx={{ mb: 4 }}>
                    {!car?.source?.type === 'Manual' && (
                      <>
                        <strong>
                          {car.make} {car.model}&nbsp;
                        </strong>
                        <Typography variant="h5" component="span">
                          {car.version}
                        </Typography>
                      </>
                    )}
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      m: '0 auto',
                      pb: 6,
                    }}
                  >
                    {car?.imageUrl && (
                      <img
                        src={car.imageUrl}
                        alt={`${car.make} ${car.model} ${car.version}`}
                        style={{ width: '100%' }}
                      />
                    )}
                    {isImgBtnLoading ? (
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        loading={isImgBtnLoading}
                        sx={({ spacing }) => ({
                          position: 'absolute',
                          top: spacing(2),
                          right: spacing(2),
                          py: {
                            xs: 2,
                            md: 2,
                            lg: 2,
                          },
                          px: {
                            xs: 6,
                            md: 6,
                            lg: 6,
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#dcdcdc',
                          },
                        })}
                      />
                    ) : (
                      <Button
                        size="small"
                        component="label"
                        variant="contained"
                        color="primary"
                        sx={({ spacing }) => ({
                          position: 'absolute',
                          top: spacing(2),
                          right: spacing(2),
                          borderRadius: 0,
                          textAlign: 'center',
                        })}
                      >
                        <input
                          name="image"
                          hidden
                          accept="image/*"
                          multiple
                          type="file"
                          onChange={handleImageChange}
                          style={{
                            display: 'none',
                          }}
                        />
                        {car?.imageUrl
                          ? t('create:car.form.input.changePhoto.label')
                          : t('create:car.form.input.addPhoto.label')}
                      </Button>
                    )}
                  </Box>
                </Box>
              </StyledPaper>
            </Grid>
          )}
          <Grid item xs={12} lg={6}>
            <StyledPaper>
              <CarDataTable
                onInputChange={handleInputChange}
                data={carDataTable}
              />
            </StyledPaper>
          </Grid>
        </Grid>
        <Portal container={portalRef.current}>
          <ButtonBox
            buttons={[
              {
                label: t('backToListBtnLabel'),
                icon: <CancelIcon />,
                props: {
                  variant: 'outlined',
                  color: 'primary',
                  onClick: onCancelClick,
                },
              },
              {
                label: 'Zapisz',
                icon: <SaveIcon fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: saveBtnLoading,
                  onClick: saveCarBasicData(),
                },
              },
              {
                label: 'Zakończ',
                icon: <CheckIcon fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: saveBtnLoading,
                  onClick: saveCarBasicData(`/controls/${controlId}/summary`),
                },
              },
              {
                label: t('nextBtnLabel'),
                icon: <ArrowRight fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: saveBtnLoading,
                  onClick: saveCarBasicData(
                    `/controls/${controlId}/edit/guarantee`,
                  ),
                },
              },
            ]}
          />
        </Portal>
      </Section>
    </div>
  )
}

export default ControlsEditCar
